import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/graphql";
import {
  GET_DUTY_COLUMNS,
  GET_DUTY_COLUMNS_FOR_ORDERING,
  GET_DUTY_COLUMN,
  CREATE_DUTY_COLUMN,
  UPDATE_DUTY_COLUMN,
  DELETE_DUTY_COLUMN,
  ADD_RULE_TO_DUTY_COLUMN,
  UPDATE_RULE_IN_DUTY_COLUMN,
  REMOVE_RULE_FROM_DUTY_COLUMN,
  GET_AUTHORITY_TIMELINE,
} from "@/graphql/product-catalogue/duty-columns";
import { DutyRuleDelta } from "@/utils/calculate-duty-rule-delta";

// Enumerations for enhanced duty system
export enum RateType {
  ADVALOREM = "ADVALOREM",      // Percentage of value
  SPECIFIC = "SPECIFIC",        // Fixed amount per unit
  REPLACEMENT = "REPLACEMENT",  // Replaces all other rates
}

export enum ValueBasis {
  ENTERED_VALUE = "ENTERED_VALUE",        // Full customs value
  STEEL_PORTION = "STEEL_PORTION",        // Steel percentage only
  ALUMINUM_PORTION = "ALUMINUM_PORTION",  // Aluminum percentage only
  QUOTA_TIER = "QUOTA_TIER",              // Based on quota tier
}

export enum ExclusiveMode {
  FIRST_WINS = "FIRST_WINS",  // First applied wins
  MAX_RATE = "MAX_RATE",      // Highest rate wins
  LAST_WINS = "LAST_WINS",    // Last applied wins
}

// Common Authority Values
export const AUTHORITIES = [
  "MFN",                // Most Favored Nation
  "SECTION_201",        // Safeguard duties
  "SECTION_232",        // National security (steel/aluminum)
  "SECTION_301",        // Trade Act remedies
  "IEEPA-Reciprocal",   // Emergency powers - Reciprocal
  "IEEPA-Fent",         // Emergency powers - Fentanyl
  "IEEPA-Russian-Oil",  // Emergency powers - Russian Oil
  "IEEPA-Brazil",       // Emergency powers - Brazil
  "AD",                 // Anti-dumping
  "CVD",                // Countervailing duties
];

export type Authority = typeof AUTHORITIES[number];

// Type definitions
export interface DutyRule {
  _id: string;
  priority: number;
  countries: string[];
  countryExclusions: string[];
  htsCodes: string[];
  exclusions: string[];
  rate: number;
  startDate: string;
  endDate?: string;
  // New fields for enhanced system
  authority?: Authority;
  rateType?: RateType;
  valueBasis?: ValueBasis;
  isMetal?: boolean;
  exclusiveGroup?: string;
  exclusiveMode?: ExclusiveMode;
  allowedStacksWith?: string[];
  nonMetalAllowedStacksWith?: string[];
  requiredWith?: string[];
  ceilingPercent?: number;
  ceilingAgainstAuthority?: string;
}

export interface DutyColumn {
  _id: string;
  name: string;
  description?: string;
  ch99Codes?: string[];
  citations?: Citation[];
  isActive: boolean;
  displayOrder: number;
  columnPriority: number;
  isStackable: boolean;
  mutuallyExclusiveGroup?: string;
  rules?: DutyRule[];
  createdAt: string;
  updatedAt: string;
}

export interface Citation {
  url: string;
  postDate?: string;
}

export interface DutyColumnFilterInput {
  name?: string;
  isActive?: boolean;
  authorities?: Authority[];
  pageNo?: number;
  pageSize?: number;
}

export interface DutyColumnInput {
  name: string;
  description?: string;
  ch99Codes?: string[];
  citations?: CitationInput[];
  isActive?: boolean;
  displayOrder?: number;
  columnPriority: number;
  isStackable: boolean;
  mutuallyExclusiveGroup?: string;
  rules?: DutyRuleInput[];
}

export interface DutyRuleUpdate extends DutyRuleInput {
  _id: string;
}

export type DutyRuleUpdatePayload = DutyRuleDelta

export interface DutyColumnUpdateInput {
  name?: string;
  description?: string;
  ch99Codes?: string[];
  citations?: CitationInput[];
  isActive?: boolean;
  displayOrder?: number;
  columnPriority?: number;
  isStackable?: boolean;
  mutuallyExclusiveGroup?: string;
  rules?: DutyRuleUpdate[];
}

export interface CitationInput {
  url: string;
  postDate?: string;
}

export interface DutyRuleInput {
  priority: number;
  countries: string[];
  countryExclusions: string[];
  htsCodes: string[];
  exclusions: string[];
  rate: number;
  startDate: string;
  endDate?: string;
  // New fields for enhanced system
  authority?: Authority;
  rateType?: RateType;
  valueBasis?: ValueBasis;
  isMetal?: boolean;
  exclusiveGroup?: string;
  exclusiveMode?: ExclusiveMode;
  allowedStacksWith?: string[];
  nonMetalAllowedStacksWith?: string[];
  requiredWith?: string[];
  ceilingPercent?: number;
  ceilingAgainstAuthority?: string;
}

export interface DutyColumnsResponse {
  data: DutyColumn[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface PaginatedRulesResponse {
  data: DutyRule[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface DutyColumnWithPaginatedRules extends Omit<DutyColumn, 'rules'> {
  rules?: PaginatedRulesResponse;
}

export interface SingleDutyColumnResponse {
  data: DutyColumn | DutyColumnWithPaginatedRules;
}

// Simplified type for column ordering
export interface DutyColumnForOrdering {
  _id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

export interface DutyColumnsForOrderingResponse {
  data: DutyColumnForOrdering[];
}

// Authority Timeline types
export interface ActiveColumn {
  name: string;
  priority: number;
  effectiveRate: number;
  ruleId: string;
  ruleNumber: number;
}

export interface AuthorityPeriod {
  startDate: string;
  endDate: string;
  activeColumns: ActiveColumn[];
}

export interface AuthorityTimelineItem {
  authority: Authority;
  periods: AuthorityPeriod[];
}

export type AuthorityTimelineResponse = AuthorityTimelineItem[];

interface GetDutyColumnParams {
  id: string;
  rulesPageNo?: number;
  rulesPageSize?: number;
}

export const dutyColumnsApi = createApi({
  reducerPath: "api/dutyColumns",
  tagTypes: ["DutyColumn"],
  baseQuery,
  endpoints: (builder) => ({
    getDutyColumns: builder.query<DutyColumnsResponse, DutyColumnFilterInput | void>({
      query: (filter = {}) => ({
        document: GET_DUTY_COLUMNS,
        variables: { filter },
      }),
      transformResponse: (response: { dutyColumns: DutyColumnsResponse }) =>
        response.dutyColumns,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: "DutyColumn" as const, id: _id })),
              { type: "DutyColumn", id: "LIST" },
            ]
          : [{ type: "DutyColumn", id: "LIST" }],
    }),

    getDutyColumn: builder.query<SingleDutyColumnResponse, GetDutyColumnParams>({
      query: ({ id, rulesPageNo, rulesPageSize }) => ({
        document: GET_DUTY_COLUMN,
        variables: { id, rulesPageNo, rulesPageSize },
      }),
      transformResponse: (response: { dutyColumn: SingleDutyColumnResponse }) =>
        response.dutyColumn,
      providesTags: (_, __, { id }) => [{ type: "DutyColumn", id }],
    }),

    createDutyColumn: builder.mutation<SingleDutyColumnResponse, DutyColumnInput>({
      query: (dutyColumn) => ({
        document: CREATE_DUTY_COLUMN,
        variables: { dutyColumn },
      }),
      transformResponse: (response: { createDutyColumn: SingleDutyColumnResponse }) =>
        response.createDutyColumn,
      invalidatesTags: [{ type: "DutyColumn", id: "LIST" }],
    }),

    updateDutyColumn: builder.mutation<SingleDutyColumnResponse, { id: string; dutyColumn: DutyColumnUpdateInput }>({
      query: ({ id, dutyColumn }) => ({
        document: UPDATE_DUTY_COLUMN,
        variables: { id, dutyColumn },
      }),
      transformResponse: (response: { updateDutyColumn: SingleDutyColumnResponse }) =>
        response.updateDutyColumn,
      async onQueryStarted({ id, dutyColumn }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dutyColumnsApi.util.updateQueryData('getDutyColumn', { id, rulesPageNo: 1, rulesPageSize: 5 }, (draft) => {
            if (draft.data) {
              Object.assign(draft.data, dutyColumn);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { id }) => [
        { type: "DutyColumn", id },
        { type: "DutyColumn", id: "LIST" },
        { type: "DutyColumn", id: "ORDERING" },
      ],
    }),

    deleteDutyColumn: builder.mutation<boolean, string>({
      query: (id) => ({
        document: DELETE_DUTY_COLUMN,
        variables: { id },
      }),
      transformResponse: (response: { deleteDutyColumn: boolean }) =>
        response.deleteDutyColumn,
      invalidatesTags: (_, __, id) => [
        { type: "DutyColumn", id },
        { type: "DutyColumn", id: "LIST" },
      ],
    }),

    addRuleToDutyColumn: builder.mutation<SingleDutyColumnResponse, { columnId: string; rule: DutyRuleInput }>({
      query: ({ columnId, rule }) => ({
        document: ADD_RULE_TO_DUTY_COLUMN,
        variables: { columnId, rule },
      }),
      transformResponse: (response: { addRuleToDutyColumn: SingleDutyColumnResponse }) =>
        response.addRuleToDutyColumn,
      invalidatesTags: (_, __, { columnId }) => [
        { type: "DutyColumn", id: columnId },
        { type: "DutyColumn", id: "LIST" },
      ],
    }),

    updateRuleInDutyColumn: builder.mutation<SingleDutyColumnResponse, { columnId: string; ruleId: string; rule: DutyRuleUpdatePayload }>({
      query: ({ columnId, ruleId, rule }) => ({
        document: UPDATE_RULE_IN_DUTY_COLUMN,
        variables: { columnId, ruleId, rule },
      }),
      transformResponse: (response: { updateRuleInDutyColumn: SingleDutyColumnResponse }) =>
        response.updateRuleInDutyColumn,
      async onQueryStarted({ columnId, ruleId, rule: delta }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dutyColumnsApi.util.updateQueryData('getDutyColumn', { id: columnId, rulesPageNo: 1, rulesPageSize: 5 }, (draft) => {
            if (draft.data && draft.data.rules && 'data' in draft.data.rules) {
              const ruleToUpdate = draft.data.rules.data.find((r: DutyRule) => r._id === ruleId);
              if (ruleToUpdate) {
                // This is a more robust optimistic update
                Object.assign(ruleToUpdate, delta);
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { columnId }) => [
        { type: "DutyColumn", id: columnId },
        { type: "DutyColumn", id: "LIST" },
      ],
    }),

    removeRuleFromDutyColumn: builder.mutation<SingleDutyColumnResponse, { columnId: string; ruleId: string }>({
      query: ({ columnId, ruleId }) => ({
        document: REMOVE_RULE_FROM_DUTY_COLUMN,
        variables: { columnId, ruleId },
      }),
      transformResponse: (response: { removeRuleFromDutyColumn: SingleDutyColumnResponse }) =>
        response.removeRuleFromDutyColumn,
      async onQueryStarted({ columnId, ruleId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dutyColumnsApi.util.updateQueryData('getDutyColumn', { id: columnId, rulesPageNo: 1, rulesPageSize: 5 }, (draft) => {
            if (draft.data && draft.data.rules && 'data' in draft.data.rules) {
              draft.data.rules.data = draft.data.rules.data.filter((r: DutyRule) => r._id !== ruleId);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { columnId }) => [
        { type: "DutyColumn", id: columnId },
        { type: "DutyColumn", id: "LIST" },
      ],
    }),

    getDutyColumnsForOrdering: builder.query<DutyColumnsForOrderingResponse, void>({
      query: () => ({
        document: GET_DUTY_COLUMNS_FOR_ORDERING,
        variables: { filter: { pageNo: 1, pageSize: 500, isActive: undefined } },
      }),
      transformResponse: (response: { dutyColumns: DutyColumnsForOrderingResponse }) =>
        response.dutyColumns,
      providesTags: [{ type: "DutyColumn", id: "ORDERING" }],
    }),

    getAuthorityTimeline: builder.query<AuthorityTimelineResponse, void>({
      query: () => ({
        document: GET_AUTHORITY_TIMELINE,
      }),
      transformResponse: (response: { getAuthorityTimeline: AuthorityTimelineResponse }) =>
        response.getAuthorityTimeline,
      providesTags: [{ type: "DutyColumn", id: "TIMELINE" }],
    }),
  }),
});

export const {
  useGetDutyColumnsQuery,
  useGetDutyColumnsForOrderingQuery,
  useGetDutyColumnQuery,
  useCreateDutyColumnMutation,
  useUpdateDutyColumnMutation,
  useDeleteDutyColumnMutation,
  useAddRuleToDutyColumnMutation,
  useUpdateRuleInDutyColumnMutation,
  useRemoveRuleFromDutyColumnMutation,
  useGetAuthorityTimelineQuery,
} = dutyColumnsApi; 